import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import {
  Lock,
  KeyRound,
  Mail,
  Check,
  ShieldAlert,
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  RotateCcw,
  Sparkles,
  Heart,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';
import { romanticAudio } from '../utils/audio';
import {
  requestPasswordResetCode,
  verifyPasswordResetCode,
  submitPasswordResetWithToken,
} from '../services/authService';

interface ResetPasswordPageProps {
  onNavigateToLogin: () => void;
  onNavigateToHome?: () => void;
}

type ResetStep = 'request' | 'verify' | 'new-password' | 'success';

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  onNavigateToLogin,
  onNavigateToHome,
}) => {
  // Step State
  const [step, setStep] = useState<ResetStep>('request');

  // Input states
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Status and Feedback
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);

  // Timers
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeExpirationTimer, setCodeExpirationTimer] = useState(600); // 10 minutes (600s)

  // Input references for 6-digit OTP
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // 60-second cooldown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // 10-minute code expiration timer (runs only while on verify step)
  useEffect(() => {
    if (step !== 'verify' || codeExpirationTimer <= 0) return;
    const interval = setInterval(() => {
      setCodeExpirationTimer((prev) => {
        if (prev <= 1) {
          setErrorMessage('Este código ha expirado. Por favor solicita uno nuevo.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, codeExpirationTimer]);

  // Auto-focus first digit input when entering verify step
  useEffect(() => {
    if (step === 'verify') {
      const timer = setTimeout(() => {
        otpInputsRef.current[0]?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // Helper to format MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to mask email for privacy
  const getMaskedEmail = (rawEmail: string) => {
    const [user, domain] = rawEmail.split('@');
    if (!user || !domain) return rawEmail;
    if (user.length <= 2) return `${user[0]}***@${domain}`;
    const visibleChars = user.length > 4 ? 2 : 1;
    const start = user.slice(0, visibleChars);
    const end = user.slice(-visibleChars);
    return `${start}•••${end}@${domain}`;
  };

  // Calculate password strength
  const getPasswordStrength = (pass: string) => {
    if (!pass) return { score: 0, label: '', color: 'bg-slate-200' };
    let score = 0;
    if (pass.length >= 6) score += 1;
    if (pass.length >= 8) score += 1;
    if (/[A-Z]/.test(pass)) score += 1;
    if (/[0-9]/.test(pass)) score += 1;
    if (/[^A-Za-z0-9]/.test(pass)) score += 1;

    if (score <= 2) return { score: 1, label: 'Débil', color: 'bg-amber-400' };
    if (score <= 4) return { score: 2, label: 'Media', color: 'bg-emerald-400' };
    return { score: 3, label: 'Fuerte', color: 'bg-rose-500' };
  };

  // -------------------------------------------------------------
  // PASO 1: Solicitar Código OTP
  // -------------------------------------------------------------
  const handleRequestCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setErrorMessage('Introduce el correo electrónico asociado a tu cuenta.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setErrorMessage('El formato de correo electrónico no es válido.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setNoticeMessage('');
    setAttemptsLeft(null);

    try {
      const res = await requestPasswordResetCode(cleanEmail);
      if (res.success) {
        romanticAudio.playBloomSuccess();
        setStep('verify');
        setOtpDigits(['', '', '', '', '', '']);
        setResendCooldown(60);
        setCodeExpirationTimer(600); // 10 minutes
        setNoticeMessage(res.message || 'Hemos enviado un código de 6 dígitos a tu correo.');
      } else {
        setErrorMessage(res.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.');
      }
    } catch {
      setErrorMessage('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------------------------------------------------
  // PASO 2: Manejo de casillas de 6 dígitos OTP
  // -------------------------------------------------------------
  const handleDigitChange = (index: number, val: string) => {
    const cleanChar = val.replace(/\D/g, '').slice(-1);
    const updated = [...otpDigits];
    updated[index] = cleanChar;
    setOtpDigits(updated);
    if (errorMessage) setErrorMessage('');

    // Advance to next box
    if (cleanChar && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otpDigits[index] && index > 0) {
        const updated = [...otpDigits];
        updated[index - 1] = '';
        setOtpDigits(updated);
        otpInputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const updated = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      updated[i] = pasted[i] || '';
    }
    setOtpDigits(updated);
    if (errorMessage) setErrorMessage('');

    const nextEmpty = updated.findIndex((d) => d === '');
    if (nextEmpty !== -1 && nextEmpty < 6) {
      otpInputsRef.current[nextEmpty]?.focus();
    } else {
      otpInputsRef.current[5]?.focus();
    }
  };

  // Submit and verify code
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (codeExpirationTimer <= 0) {
      setErrorMessage('Este código ha expirado. Por favor solicita uno nuevo.');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const fullCode = otpDigits.join('');

    if (fullCode.length < 6) {
      setErrorMessage('Introduce los 6 dígitos del código.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const res = await verifyPasswordResetCode(cleanEmail, fullCode);
      if (res.success && res.resetToken) {
        romanticAudio.playChime();
        setResetToken(res.resetToken);
        setStep('new-password');
        setNoticeMessage('Código verificado correctamente.');
      } else {
        romanticAudio.playNote(220, 0.2, 'sawtooth', 0.05);
        if (res.attemptsLeft !== undefined) {
          setAttemptsLeft(res.attemptsLeft);
          if (res.attemptsLeft <= 0) {
            setErrorMessage('Has superado el número máximo de intentos. Por favor solicita un nuevo código.');
          } else {
            setErrorMessage(`Código incorrecto. Te ${res.attemptsLeft === 1 ? 'queda' : 'quedan'} ${res.attemptsLeft} ${res.attemptsLeft === 1 ? 'intento' : 'intentos'}.`);
          }
        } else {
          setErrorMessage(res.error || 'Código incorrecto. Revisa el código e inténtalo nuevamente.');
        }
      }
    } catch {
      setErrorMessage('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------------------------------------------------
  // PASO 3: Guardar Nueva Contraseña
  // -------------------------------------------------------------
  const handleSaveNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPass = newPassword.trim();
    const cleanConfirm = confirmPassword.trim();

    if (cleanPass.length < 6) {
      setErrorMessage('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (cleanPass !== cleanConfirm) {
      setErrorMessage('Las contraseñas no coinciden.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const res = await submitPasswordResetWithToken(resetToken, cleanPass);
      if (res.success) {
        romanticAudio.playBloomSuccess();
        confetti({
          particleCount: 100,
          spread: 80,
          origin: { y: 0.6 },
          colors: ['#f43f5e', '#fb7185', '#fbbf24', '#f59e0b', '#38bdf8', '#34d399'],
        });
        setStep('success');
      } else {
        setErrorMessage(res.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.');
      }
    } catch {
      setErrorMessage('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-rose-50/50 via-white to-amber-50/30 text-slate-800 flex flex-col justify-between items-center p-4 sm:p-6 lg:p-8 font-['Plus_Jakarta_Sans',sans-serif] relative overflow-x-hidden selection:bg-rose-100 selection:text-rose-900">
      {/* Background Decorative Blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-rose-200/25 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-32 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 left-1/3 w-96 h-96 bg-rose-100/30 rounded-full blur-3xl" />
      </div>

      {/* Top Header Navigation */}
      <header className="w-full max-w-xl mx-auto flex items-center justify-between relative z-10 py-2 sm:py-4">
        <button
          type="button"
          onClick={onNavigateToLogin}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white/80 hover:bg-white border border-slate-200/80 px-3.5 py-2 rounded-xl shadow-xs transition-all cursor-pointer backdrop-blur-md"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-rose-500" />
          <span>Volver al acceso</span>
        </button>

        {onNavigateToHome && (
          <button
            type="button"
            onClick={onNavigateToHome}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-rose-600 transition-colors cursor-pointer"
          >
            <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-100" />
            <span>Nuestros Recuerdos</span>
          </button>
        )}
      </header>

      {/* Main Recovery Card */}
      <main className="w-full max-w-md mx-auto my-auto relative z-10 py-4 sm:py-6">
        <div className="bg-white/95 backdrop-blur-xl border border-rose-100/80 rounded-3xl p-6 sm:p-8 shadow-xl shadow-rose-950/5 relative overflow-hidden">
          {/* Subtle Top Rose Highlight Line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-400 via-rose-500 to-amber-400" />

          {/* Step Progress Indicators (1 -> 2 -> 3) */}
          {step !== 'success' && (
            <div className="mb-6 pb-5 border-b border-slate-100">
              <div className="flex items-center justify-between max-w-xs mx-auto">
                {/* Step 1 */}
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step === 'request'
                        ? 'bg-rose-500 text-white shadow-md shadow-rose-500/25 ring-4 ring-rose-100'
                        : 'bg-emerald-500 text-white'
                    }`}
                  >
                    {step === 'request' ? '1' : <Check className="w-4 h-4" />}
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">Correo</span>
                </div>

                <div
                  className={`h-0.5 flex-1 mx-2 transition-all ${
                    step === 'request' ? 'bg-slate-200' : 'bg-emerald-400'
                  }`}
                />

                {/* Step 2 */}
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step === 'verify'
                        ? 'bg-rose-500 text-white shadow-md shadow-rose-500/25 ring-4 ring-rose-100'
                        : step === 'new-password'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-100 text-slate-400 border border-slate-200'
                    }`}
                  >
                    {step === 'new-password' ? <Check className="w-4 h-4" /> : '2'}
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">Código</span>
                </div>

                <div
                  className={`h-0.5 flex-1 mx-2 transition-all ${
                    step === 'new-password' ? 'bg-emerald-400' : 'bg-slate-200'
                  }`}
                />

                {/* Step 3 */}
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step === 'new-password'
                        ? 'bg-rose-500 text-white shadow-md shadow-rose-500/25 ring-4 ring-rose-100'
                        : 'bg-slate-100 text-slate-400 border border-slate-200'
                    }`}
                  >
                    3
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">Contraseña</span>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Notices */}
          {noticeMessage && (
            <div className="mb-4 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
              <span className="leading-relaxed font-medium">{noticeMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="mb-4 p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" />
              <div className="flex-1">
                <span className="font-medium leading-relaxed block">{errorMessage}</span>
                {attemptsLeft !== null && attemptsLeft > 0 && (
                  <span className="text-[11px] text-rose-600 block mt-0.5">
                    Intentos disponibles: <strong>{attemptsLeft} de 5</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* PASO 1: SOLICITAR CÓDIGO                                  */}
          {/* ========================================================= */}
          {step === 'request' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-3 border border-rose-100 shadow-xs">
                  <KeyRound className="w-7 h-7 text-rose-500" />
                </div>
                <h2 className="font-['Playfair_Display',serif] text-2xl font-bold text-slate-900 tracking-tight">
                  Recuperar contraseña
                </h2>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-xs mx-auto">
                  Introduce el correo electrónico asociado a tu cuenta y te enviaremos un código de verificación.
                </p>
              </div>

              <form onSubmit={handleRequestCode} className="space-y-4">
                <div>
                  <label
                    htmlFor="recovery-email"
                    className="text-[11px] font-bold text-slate-700 tracking-wider uppercase block mb-1.5"
                  >
                    Correo Electrónico
                  </label>
                  <div className="relative">
                    <input
                      id="recovery-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      placeholder="tu-correo@ejemplo.com"
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm transition-all outline-none"
                      required
                      autoFocus
                      autoComplete="email"
                      disabled={isLoading}
                    />
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="w-full py-3.5 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Enviando código…</span>
                      </>
                    ) : (
                      <>
                        <span>Enviar código</span>
                        <Sparkles className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="text-xs text-slate-500 hover:text-slate-800 font-medium transition-colors cursor-pointer"
                >
                  ¿Recordaste tu contraseña? <span className="text-rose-600 font-bold hover:underline">Iniciar sesión</span>
                </button>
              </div>
            </motion.div>
          )}

          {/* ========================================================= */}
          {/* PASO 2: VERIFICAR CÓDIGO (6 DÍGITOS OTP)                  */}
          {/* ========================================================= */}
          {step === 'verify' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-3 border border-rose-100 shadow-xs">
                  <Mail className="w-7 h-7 text-rose-500" />
                </div>
                <h2 className="font-['Playfair_Display',serif] text-2xl font-bold text-slate-900 tracking-tight">
                  Verifica tu correo
                </h2>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-xs mx-auto">
                  Enviamos un código de 6 dígitos a tu correo electrónico:<br />
                  <strong className="text-slate-800 font-semibold break-all text-xs">
                    {getMaskedEmail(email)}
                  </strong>
                </p>
              </div>

              {/* Code Validity Timer Bar */}
              <div className="py-2.5 px-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs mb-4">
                {codeExpirationTimer > 0 ? (
                  <span className="text-slate-600 font-medium flex items-center gap-1.5 text-[11px]">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Código válido durante <strong>{formatTime(codeExpirationTimer)}</strong></span>
                  </span>
                ) : (
                  <span className="text-rose-600 font-semibold flex items-center gap-1.5 text-[11px]">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    <span>Este código ha expirado.</span>
                  </span>
                )}

                {codeExpirationTimer <= 0 && (
                  <button
                    type="button"
                    onClick={() => handleRequestCode()}
                    disabled={isLoading}
                    className="text-[11px] font-bold text-rose-600 hover:text-rose-700 underline cursor-pointer"
                  >
                    Nuevo código
                  </button>
                )}
              </div>

              <form onSubmit={handleVerifyCode} className="space-y-5">
                {/* 6 Casillas Numéricas Independientes */}
                <div>
                  <label className="text-[11px] font-bold text-slate-700 tracking-wider uppercase block text-center mb-2.5">
                    Ingresa los 6 dígitos
                  </label>
                  <div
                    className="flex items-center justify-center gap-1.5 sm:gap-2.5"
                    onPaste={handleDigitPaste}
                  >
                    {otpDigits.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => {
                          otpInputsRef.current[idx] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        disabled={codeExpirationTimer <= 0 || isLoading}
                        onChange={(e) => handleDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                        className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-mono font-bold text-slate-900 bg-slate-50 hover:bg-slate-100/80 focus:bg-white border-2 border-slate-200 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 rounded-2xl transition-all outline-none disabled:opacity-40"
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2.5 pt-1">
                  <button
                    type="submit"
                    disabled={isLoading || otpDigits.join('').length < 6 || codeExpirationTimer <= 0}
                    className="w-full py-3.5 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Verificando código…</span>
                      </>
                    ) : (
                      <>
                        <span>Verificar código</span>
                        <Check className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {/* Resend Cooldown Protection */}
                  <div className="text-center pt-1">
                    {resendCooldown > 0 ? (
                      <span className="text-[11px] text-slate-400 font-medium">
                        Puedes solicitar un nuevo código en {resendCooldown} s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRequestCode()}
                        disabled={isLoading}
                        className="text-xs text-rose-600 hover:text-rose-700 font-semibold hover:underline inline-flex items-center gap-1.5 cursor-pointer transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5 text-rose-500" />
                        <span>Reenviar código</span>
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setStep('request');
                      setErrorMessage('');
                      setNoticeMessage('');
                    }}
                    className="w-full py-2.5 text-xs text-slate-500 hover:text-slate-800 font-medium rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    Cambiar correo electrónico
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* ========================================================= */}
          {/* PASO 3: NUEVA CONTRASEÑA                                  */}
          {/* ========================================================= */}
          {step === 'new-password' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-3 border border-rose-100 shadow-xs">
                  <Lock className="w-7 h-7 text-rose-500" />
                </div>
                <h2 className="font-['Playfair_Display',serif] text-2xl font-bold text-slate-900 tracking-tight">
                  Crear nueva contraseña
                </h2>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-xs mx-auto">
                  Crea una nueva contraseña segura para tu cuenta de Nuestros Recuerdos.
                </p>
              </div>

              <form onSubmit={handleSaveNewPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="page-new-pass"
                    className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center justify-between mb-1.5"
                  >
                    <span>Nueva contraseña</span>
                    {newPassword && (
                      <span className={`text-[10px] font-bold ${strength.color.replace('bg-', 'text-')}`}>
                        {strength.label}
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      id="page-new-pass"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      placeholder="••••••••••••"
                      className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                      required
                      autoComplete="new-password"
                      autoFocus
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                      aria-label={showNewPassword ? 'Ocultar' : 'Mostrar'}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator Bar */}
                  {newPassword && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <div className={`h-1.5 flex-1 rounded-full ${strength.score >= 1 ? strength.color : 'bg-slate-100'}`} />
                      <div className={`h-1.5 flex-1 rounded-full ${strength.score >= 2 ? strength.color : 'bg-slate-100'}`} />
                      <div className={`h-1.5 flex-1 rounded-full ${strength.score >= 3 ? strength.color : 'bg-slate-100'}`} />
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="page-confirm-pass"
                    className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center justify-between mb-1.5"
                  >
                    <span>Confirmar nueva contraseña</span>
                    {confirmPassword && (
                      <span className={`text-[10px] font-bold ${passwordsMatch ? 'text-emerald-600' : 'text-rose-500'}`}>
                        {passwordsMatch ? 'Coinciden' : 'No coinciden'}
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      id="page-confirm-pass"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      placeholder="••••••••••••"
                      className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                      required
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                      aria-label={showConfirmPassword ? 'Ocultar' : 'Mostrar'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="text-[11px] text-slate-400 leading-relaxed pl-1">
                  Mínimo 6 caracteres. Ambas contraseñas deben ser idénticas.
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading || newPassword.length < 6 || newPassword !== confirmPassword}
                    className="w-full py-3.5 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Actualizando contraseña…</span>
                      </>
                    ) : (
                      <>
                        <span>Actualizar contraseña</span>
                        <ShieldCheck className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* ========================================================= */}
          {/* PASO 4: ÉXITO - CONTRASEÑA ACTUALIZADA                   */}
          {/* ========================================================= */}
          {step === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center py-4"
            >
              <div className="w-20 h-20 rounded-3xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-5 border-2 border-emerald-100 shadow-md shadow-emerald-500/10">
                <Check className="w-10 h-10 text-emerald-600 stroke-[2.5]" />
              </div>

              <h2 className="font-['Playfair_Display',serif] text-2xl font-bold text-slate-900 tracking-tight">
                Contraseña actualizada correctamente.
              </h2>

              <p className="text-xs text-slate-600 mt-2.5 leading-relaxed max-w-xs mx-auto">
                Tu contraseña ha sido actualizada de forma segura en Firebase Authentication. Ya puedes iniciar sesión con tu nueva contraseña.
              </p>

              <div className="mt-7">
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="w-full py-3.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-slate-900/10 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Volver al inicio de sesión</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-md mx-auto text-center py-4 relative z-10">
        <p className="text-[11px] text-slate-400">
          Nuestros Recuerdos • Espacio privado de pareja protegido
        </p>
      </footer>
    </div>
  );
};
